<template lang="pug">
div
	h1 {{contentpage.name}}
	ws-content(:value="contentpage.content")
		v-row
			v-col(cols="2")
				v-select(v-model="filters.sport" :label="$t('page.areas._area.skischools:filter_sport')" :items="filterSportItems")
			v-col(cols="2")
				v-select(v-model="filters.level" :label="$t('page.areas._area.skischools:filter_level')" :items="filterLevelItems")
			v-col(cols="2")
				v-select(v-model="filters.age" :label="$t('page.areas._area.skischools:filter_age')" :items="filterAgeItems")
			v-col(cols="2")
				v-select(v-model="filters.lessonType" :label="$t('page.areas._area.skischools:filter_lessonType')" :items="filterLessonTypeItems")

		template(v-for="skischool in skischools")
			v-row.mb-2.mt-6
				v-col.grow.pb-0
					.font-weight-bold {{skischool.name}}
						span
							ws-icon(small).grey--text map-marker
						span.caption.grey--text {{capitalized(skischool.resort_name)}}
				v-col.pb-0.shrink(v-if="skischool.partner.image")
					span.caption.grey--text.mr-2.text-no-wrap
						| via
						v-img.ml-2.d-inline-block(style="vertical-align: bottom" :src="skischool.partner.image" width="70" height="24" contain)
						span.ml-2(v-if="skischool.partner.slug === 'checkyeti'") De nummer 1 voor skilessen
			v-list(v-if="skischool.products" subheading dense)
				template(v-for="product in skischool.products")
					v-divider
					a(:href="`/redir?u=${encodeURIComponent(product.url)}&p=${skischool.partner.slug}&i=${product.id}&t=skischool&h=${$redirHash(product.url)}`" target="_blank" rel="nofollow")
						v-list-item.pl-2.pr-0
							v-list-item-content
								v-list-item-title(style="font-size:15px")
									span.primary--text {{product.name}}&nbsp;
									span(v-if="skischool.partner.slug === 'checkyeti'") {{$t('page.areas._area.lessons:price_prefix')}}&nbsp;
									span(v-else) {{$t('page.areas._area.lessons:price_prefix_from')}}&nbsp;
									span(v-if="skischool.partner.slug === 'checkyeti'").warning--text.font-weight-bold {{$n(product.price, 'simplecurrency', $i18n.locale)}} per dag
									span(v-else).warning--text.font-weight-bold {{$n(product.price, 'simplecurrency', $i18n.locale)}}
									span(v-if="product.resort").ml-2.caption.grey--text ({{product.resort.name}})
							v-list-item-action.ma-0
								v-btn(small depressed color="warning") {{$t('page.areas._area.rental:check_button')}}

	client-only
		mapbox.mt-6(
			ref="map"
			style="height:600px"
			:layers="layers"
			:sources="sources"
			:fitbounds="markers.features.map(e=>e.geometry.coordinates)"
			@map-styleload="mapStyle")
			div(slot="popup")
				span.h3 {{popupData.name}}
</template>

<script>
import gql from 'graphql-tag';
import { ref } from '@vue/composition-api';
import Content from '@dengl/common/components/content.vue';
import MetaFragment from '@/components/graphql/metaFragment.graphql';
import useMap from '@/use/map';

const getAreaSnowheight = gql`
	query getAreaSnowheight($slug: String!, $path: String, $fallback: String) {
		contentpage(path: $path, fallback: $fallback) {
			id
			name
			slug
			content {
				...ContentDataFragment
			}
			...MetaFragment
		}
		area(slug: $slug) {
			id
			name
			slug
			country {
				id
				name
				countryCode
				slug
			}
			region {
				id
				name
				slug
			}
			geography {
				location {
					type
					coordinates
				}
			}
		}
	}
	${MetaFragment}
	${Content.fragments}
`;

const getSkischoolsQuery = gql`
	query getSkischoolsQuery(
		$area: ID!
		$filterSport: String
		$filterAge: String
		$filterLevel: String
		$filterLessonType: String
	) {
		area(id: $area) {
			id
			skischools(
				sport: $filterSport
				age: $filterAge
				level: $filterLevel
				lessonType: $filterLessonType
			) {
				id
				name
				resort_name
				url
				partner {
					id
					image
					name
					slug
				}
				products {
					id
					url
					price
					name
					type
					resort {
						id
						name
					}
				}
				geography {
					location {
						type
						coordinates
					}
				}
			}
		}
	}
`;

export default {
	name: 'AreaLessons',
	nuxtI18n: {
		paths: {
			'nl-NL': '/skigebieden/:area/skiles',
			'de-DE': '/skigebiete/:area/skiles',
			'nl-BE': '/skigebieden/:area/skiles'
		}
	},
	components: {
		Mapbox: () => (process.client ? import('@/components/mapbox.vue') : null)
	},
	layout: 'default',
	setup() {
		const mapItems = ref([]);
		const { markers, sources, attachListeners, popupData } = useMap(mapItems);

		const layers = [
			// {
			// 	id: 'markers',
			// 	type: 'symbol',
			// 	source: 'geojson-markers',
			// 	layout: {
			// 		'icon-image': ['get', 'icon'],
			// 		'icon-size': 0.3,
			// 		'icon-anchor': 'bottom',
			// 		'icon-allow-overlap': true
			// 	},
			// 	minzoom: 0,
			// 	maxzoom: 24
			// },
			{
				id: 'markers',
				type: 'circle',
				source: 'geojson-markers',
				layout: {
					visibility: 'visible'
				},
				paint: {
					'circle-radius': [
						'case',
						['boolean', ['feature-state', 'hover'], false],
						7,
						5
					],
					'circle-color': 'rgb(79, 164, 221)',
					'circle-stroke-color': '#fff',
					'circle-stroke-width': 2
				},
				minzoom: 0,
				maxzoom: 24
			}
		];

		return {
			mapItems,
			sources,
			layers,
			markers,
			attachListeners,
			popupData
		};
	},
	// eslint-disable-next-line @milqmedia/milq/page-has-meta
	asyncData({ params, app, route }) {
		return app.$graphClient
			.query({
				query: getAreaSnowheight,
				variables: {
					slug: params.area,
					path: route.path,
					fallback: route.path.replace(params.area, '_area')
				},
				errorPolicy: 'all'
			})
			.then(({ data, errors }) => {
				if (!data.contentpage) {
					return app.httpError(404, null, 'area-lessons-01', errors);
				}
				if (errors) {
					throw errors[0];
				}

				app.interpolater(data.contentpage.meta, ['title', 'description']);
				app.interpolater(data.contentpage.content, ['markdown']);
				app.interpolater(data.contentpage, ['name']);

				app.meta(data.contentpage.meta);
				app.ads({
					navigation: {
						visible: true,
						targets: {
							Skiresort: data.area.id,
							State: data.area.region.id,
							Country: data.area.country.id
						}
					}
				});
				app.areaBreadcrumbs(data.area, {
					[app.i18n.t(`page.areas._area.lesson:breadcrumb`)]: {
						name: 'areas-area-rentals',
						params: { area: data.area.slug }
					}
				});

				return data;
			})
			.catch(app.$destinationNotFoundCatcher)
			.catch(error => {
				return app.httpError(500, null, 'areas-area-lessons-02', error);
			});
	},
	data() {
		return {
			skischools: [],
			filters: { sport: null, age: null, level: null, lessonType: null },
			filterSportItems: [
				{
					value: 'all',
					text: this.$t('page.areas._area.skischools:filter_sport_item_all')
				},
				{
					value: 'ski',
					text: this.$t('page.areas._area.skischools:filter_sport_item_ski')
				},
				{
					value: 'snowboard',
					text: this.$t(
						'page.areas._area.skischools:filter_sport_item_snowboard'
					)
				}
			],
			filterAgeItems: [
				{
					value: 'all',
					text: this.$t('page.areas._area.skischools:filter_age_item_all')
				},
				{
					value: 'child',
					text: this.$t('page.areas._area.skischools:filter_age_item_child')
				},
				{
					value: 'adult',
					text: this.$t('page.areas._area.skischools:filter_age_item_adult')
				}
			],
			filterLevelItems: [
				{
					value: 'all',
					text: this.$t('page.areas._area.skischools:filter_level_item_all')
				},
				{
					value: 'beginner',
					text: this.$t(
						'page.areas._area.skischools:filter_level_item_beginner'
					)
				},
				{
					value: 'advanced',
					text: this.$t(
						'page.areas._area.skischools:filter_level_item_advanced'
					)
				}
			],
			filterLessonTypeItems: [
				{
					value: 'all',
					text: this.$t(
						'page.areas._area.skischools:filter_lessonType_item_all'
					)
				},
				{
					value: 'private',
					text: this.$t(
						'page.areas._area.skischools:filter_lessonType_item_private'
					)
				},
				{
					value: 'group',
					text: this.$t(
						'page.areas._area.skischools:filter_lessonType_item_group'
					)
				}
			]
		};
	},
	head() {
		return {
			...this.$structuredData()
		};
	},
	watch: {
		filters: {
			deep: true,
			handler() {
				this.fetchSkischools();
			}
		}
	},
	mounted() {
		this.fetchSkischools();
	},

	methods: {
		async fetchSkischools() {
			this.skischools = await this.$graphClient
				.query({
					query: getSkischoolsQuery,
					variables: {
						area: this.area.id,
						filterSport: this.filters.sport,
						filterAge: this.filters.age,
						filterLevel: this.filters.level,
						filterLessonType: this.filters.lessonType
					},
					errorPolicy: 'all'
				})
				.then(({ data, _errors }) => {
					this.mapItems = data.area.skischools.map(skischool => {
						return skischool;
					});

					if (this.$refs.map) {
						this.$nextTick(() => {
							this.$refs.map.fitToCurrentBounds();
						});
					}
					return data.area.skischools;
				});
		},
		mouseClick(feature) {
			if (feature.properties.url !== 'null') {
				window.open(
					`/redir?u=${encodeURIComponent(feature.properties.url)}&p=${
						feature.properties.partnerSlug
					}&i=${feature.properties.id}&t=skischool&h=${this.$redirHash(
						feature.properties.url
					)}`
				);
			}
		},
		mapStyle(map) {
			this.$refs.map.fitToCurrentBounds();
			this.attachListeners(
				map,
				this.$refs.map,
				'markers',
				'geojson-markers',
				this.mouseClick
			);
		},
		capitalized(string) {
			return string.charAt(0).toUpperCase() + string.slice(1);
		}
	}
};
</script>
